import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { X } from "lucide-react";
import { startCheckout, openBillingPortal, errorMessage } from "@/lib/api";
import { longDate } from "@/lib/time";

function priceLabel(user) {
  return user.role === "vendor" ? "$25/mo" : user.family_plan ? "$19.99/mo" : "$9.99/mo";
}

function dismissKey() {
  return `vv_trial_countdown_${new Date().toDateString()}`;
}

function shouldShow(user) {
  if (!user || user.role === "admin" || user.is_family_member || user.promo_access || user.needs_card || user.payment_issue) return false;
  if (!user.trial_active || user.trial_days_left > 3 || user.cancel_at_period_end) return false;
  return user.trialing || user.subscription_status !== "active";
}

export function countdownVisible(user) {
  return shouldShow(user) && localStorage.getItem(dismissKey()) !== "1";
}

export function TrialCountdown() {
  const { user } = useAuth();
  const [hidden, setHidden] = useState(() => localStorage.getItem(dismissKey()) === "1");
  const [busy, setBusy] = useState(false);
  if (hidden || !shouldShow(user)) return null;
  const days = user.trial_days_left;
  const ends = longDate(user.trial_ends_at);
  const act = async () => {
    setBusy(true);
    try { await (user.trialing ? openBillingPortal() : startCheckout(user.checkout_plan)); }
    catch (e) { toast.error(errorMessage(e)); setBusy(false); }
  };
  const dismiss = () => { localStorage.setItem(dismissKey(), "1"); setHidden(true); window.dispatchEvent(new Event("vv-countdown-dismissed")); };
  return (
    <div data-testid="trial-countdown" className="rise mb-6 relative overflow-hidden rounded-3xl border border-[#FFB800]/40 bg-[#151C2C] p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4">
      <div className="shrink-0 h-16 w-16 rounded-2xl bg-[#FFB800]/15 border border-[#FFB800]/40 grid place-items-center">
        <span data-testid="trial-countdown-days" className="text-3xl font-black text-[#FFB800] leading-none">{days}</span>
      </div>
      <div className="flex-1 min-w-0 pr-6">
        <p data-testid="trial-countdown-title" className="font-bold text-lg">{days} {days === 1 ? "day" : "days"} left in your free trial</p>
        <p className="text-sm text-slate-400 mt-1">
          {user.trialing
            ? `Your plan continues on ${ends} at ${priceLabel(user)} — nothing to do if you're loving it.`
            : "Add a card so your trips aren't interrupted — you won't be charged until your trial ends."}
        </p>
      </div>
      <button data-testid="trial-countdown-action" disabled={busy} onClick={act}
        className="shrink-0 rounded-full px-5 h-11 font-semibold text-sm bg-[#FFB800] text-[#0B0F19] hover:bg-[#FFB800]/90 active:scale-95 transition-transform">
        {busy ? "Opening…" : user.trialing ? "Manage billing" : "Add card"}
      </button>
      <button data-testid="trial-countdown-dismiss" onClick={dismiss} aria-label="Dismiss" className="absolute top-3 right-3 h-9 w-9 grid place-items-center rounded-full text-slate-500 hover:bg-white/5">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
